import { useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { publicStorageUrl } from "@/lib/storage-url";
import { compressImageToWebp } from "@/lib/image-compress";
import type { Database } from "@/integrations/supabase/types";
import { CheckCircle2, FileImage, Loader2, Play, Plus, Save, Trash2, Type, Upload, Video, X } from "lucide-react";
import { toast } from "sonner";
import { confirmDialog } from "@/lib/confirm-dialog";
import { AdminListToolbar } from "@/components/admin/AdminListToolbar";


type Testimonial = Database["public"]["Tables"]["testimonials"]["Row"];
type TestimonialInsert = Database["public"]["Tables"]["testimonials"]["Insert"];
type Category = Database["public"]["Enums"]["testimonial_category"];
type MediaKind = "photo" | "video";

export const Route = createFileRoute("/_admin/admin/testimonials")({
  component: AdminTestimonialsPage,
});

const MAX_IMAGE_BYTES = 10485760; // 10 MB
const ALLOWED_IMAGE_MIME = ["image/jpeg", "image/png", "image/webp"];

const ALLOWED_VIMEO_HOSTS = new Set(["vimeo.com", "www.vimeo.com", "player.vimeo.com"]);

type VimeoErrorCode =
  | "empty"
  | "whitespace"
  | "not_url"
  | "not_https"
  | "wrong_host"
  | "no_id"
  | "bad_id_format"
  | "bad_hash_format"
  | "unknown_path";

type VimeoValidation =
  | { ok: true; id: string; hash?: string }
  | { ok: false; code: VimeoErrorCode; message: string; hint?: string };

const VIMEO_FORMAT_HINT =
  "Erlaubte Formate: https://vimeo.com/123456789, https://vimeo.com/123456789/abcdef1234 oder https://player.vimeo.com/video/123456789.";

function validateVimeoUrl(input: string | null | undefined): VimeoValidation {
  const raw = (input ?? "").trim();
  if (!raw) {
    return { ok: false, code: "empty", message: "Bitte einen Vimeo-Link einfügen." };
  }

  if (/\s/.test(raw)) {
    return {
      ok: false,
      code: "whitespace",
      message: "Der Link enthält Leerzeichen oder Zeilenumbrüche.",
      hint: "Bitte nur die reine URL einfügen — ohne umliegenden Text.",
    };
  }

  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return {
      ok: false,
      code: "not_url",
      message: "Das ist keine gültige URL.",
      hint: "Erwartet wird ein vollständiger Link inkl. https:// (z. B. https://vimeo.com/123456789).",
    };
  }

  if (url.protocol !== "https:") {
    if (url.protocol === "http:") {
      return {
        ok: false,
        code: "not_https",
        message: "Der Link muss mit https:// beginnen.",
        hint: "Ihr Link nutzt http:// — bitte auf https:// ändern.",
      };
    }
    return {
      ok: false,
      code: "not_https",
      message: `Nur https://-Links sind erlaubt (gefunden: ${url.protocol}).`,
      hint: "Bitte den Link mit dem https://-Schema einfügen.",
    };
  }

  const host = url.hostname.toLowerCase();
  if (!ALLOWED_VIMEO_HOSTS.has(host)) {
    let hint: string | undefined;
    if (host === "youtu.be" || host === "youtube.com" || host === "www.youtube.com") {
      hint = "Das sieht nach einem YouTube-Link aus. Bitte einen Vimeo-Link verwenden.";
    } else if (host.endsWith("vimeocdn.com")) {
      hint = "Das ist ein Vimeo-CDN-/Thumbnail-Link, kein Video-Link. Bitte den Video-Link von vimeo.com kopieren.";
    } else if (host.endsWith("vimeo.com")) {
      hint = "Subdomains von vimeo.com werden nicht unterstützt. Bitte die Haupt-URL (vimeo.com/…) verwenden.";
    } else {
      hint = "Erlaubte Hosts: vimeo.com, www.vimeo.com, player.vimeo.com.";
    }
    return {
      ok: false,
      code: "wrong_host",
      message: `Host „${host}" ist nicht zulässig.`,
      hint,
    };
  }

  const segments = url.pathname.split("/").filter(Boolean);
  const idRe = /^[0-9]{6,12}$/;
  const looksNumeric = /^[0-9]+$/;
  const hashRe = /^[a-z0-9]{6,20}$/i;

  if (segments.length === 0) {
    return {
      ok: false,
      code: "no_id",
      message: "Im Link fehlt die Video-ID.",
      hint: "Beispiel: https://vimeo.com/123456789",
    };
  }

  let id: string | null = null;
  let hash: string | undefined;

  if (segments.length === 1) {
    const s = segments[0];
    if (idRe.test(s)) {
      id = s;
    } else if (looksNumeric.test(s)) {
      return {
        ok: false,
        code: "bad_id_format",
        message: `„${s}" ist keine gültige Vimeo-Video-ID.`,
        hint: "Erwartet werden 6–12 Ziffern (z. B. 123456789).",
      };
    } else {
      return {
        ok: false,
        code: "unknown_path",
        message: `Pfad „/${s}" wird nicht erkannt.`,
        hint: VIMEO_FORMAT_HINT,
      };
    }
  } else if (segments.length === 2 && idRe.test(segments[0])) {
    if (hashRe.test(segments[1])) {
      id = segments[0];
      hash = segments[1];
    } else {
      return {
        ok: false,
        code: "bad_hash_format",
        message: `Der Unlisted-Hash „${segments[1]}" ist ungültig.`,
        hint: "Erwartet werden 6–20 Zeichen (Buchstaben oder Ziffern).",
      };
    }
  } else if (segments.length === 2 && segments[0] === "video" && idRe.test(segments[1])) {
    id = segments[1];
  } else if (segments.length === 2 && segments[0] === "video" && looksNumeric.test(segments[1])) {
    return {
      ok: false,
      code: "bad_id_format",
      message: `„${segments[1]}" ist keine gültige Vimeo-Video-ID.`,
      hint: "Erwartet werden 6–12 Ziffern nach /video/.",
    };
  } else if (segments.length === 3 && segments[0] === "channels" && idRe.test(segments[2])) {
    id = segments[2];
  } else if (
    segments.length === 3 &&
    segments[0] === "manage" &&
    segments[1] === "videos" &&
    idRe.test(segments[2])
  ) {
    id = segments[2];
  } else {
    return {
      ok: false,
      code: "unknown_path",
      message: `Pfad „${url.pathname}" passt zu keinem bekannten Vimeo-Format.`,
      hint: VIMEO_FORMAT_HINT,
    };
  }

  // Fallback: Unlisted-Hash als Query-Param ?h=...
  if (!hash) {
    const h = url.searchParams.get("h");
    if (h !== null) {
      if (hashRe.test(h)) {
        hash = h;
      } else {
        return {
          ok: false,
          code: "bad_hash_format",
          message: `Der Unlisted-Hash „${h}" im Parameter ?h= ist ungültig.`,
          hint: "Erwartet werden 6–20 Zeichen (Buchstaben oder Ziffern).",
        };
      }
    }
  }

  return { ok: true, id: id!, ...(hash ? { hash } : {}) };
}

function extractVimeoId(url: string | null | undefined): string | null {
  const v = validateVimeoUrl(url);
  return v.ok ? v.id : null;
}


function emptyDraft(kind: MediaKind): TestimonialInsert {
  return {
    quote: "",
    author_name: "",
    author_role: "",
    categories: ["buergermeisterwahl"],
    priority: "medium",
    published: false,
    photo_path: null,
    media_kind: kind,
    video_path: null,
    video_poster_path: null,
    vimeo_url: null,
  };
}

type Priority = Database["public"]["Enums"]["testimonial_priority"];

const PRIORITY_LABELS: Record<Priority, string> = {
  high: "Hoch",
  medium: "Mittel",
  low: "Tief",
};

const PRIORITY_BADGE_CLASS: Record<Priority, string> = {
  high: "bg-primary/15 text-primary",
  medium: "bg-secondary text-foreground",
  low: "bg-muted text-muted-foreground",
};


const CATEGORY_LABELS: Record<Category, string> = {
  buergermeisterwahl: "Bürgermeisterwahlen",
  direktkandidaten: "Direktkandidaten",
  parteikampagnen: "Parteikampagnen",
  personalratswahl: "Personalratswahlen",
  "politische-kommunikation": "Politische Kommunikation",
  verbandskommunikation: "Verbandskommunikation",
  krisenkommunikation: "Krisenkommunikation",
  gemeindekommunikation: "Gemeindekommunikation",
};

const ALL_CATEGORIES = Object.keys(CATEGORY_LABELS) as Category[];

function AdminTestimonialsPage() {
  const [items, setItems] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Testimonial | null>(null);
  const [creatingKind, setCreatingKind] = useState<MediaKind | null>(null);
  const [pickingKind, setPickingKind] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<Category | "all">("all");
  const filteredItems = useMemo(
    () =>
      categoryFilter === "all"
        ? items
        : items.filter((t) => (t.categories ?? []).includes(categoryFilter)),
    [items, categoryFilter],
  );

  // Stufe 2: Freitextsuche zusätzlich zum Kategoriefilter.
  const [query, setQuery] = useState("");
  const visibleItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return filteredItems;
    return filteredItems.filter((t) =>
      [t.author_name, t.author_role, t.quote]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [filteredItems, query]);

  async function load() {
    setLoading(true);
    const { data, error } = await supabase
      .from("testimonials")
      .select("*")
      .order("priority", { ascending: true })
      .order("created_at", { ascending: false });
    if (error) setError(error.message);
    else setItems(data ?? []);
    setLoading(false);
  }

  useEffect(() => {
    load();
  }, []);

  async function onDelete(item: Testimonial) {
    if (
      !(await confirmDialog({
        title: `Testimonial von ${item.author_name} löschen?`,
        description: "Die Stimme verschwindet aus Slider und Produktseiten.",
      }))
    )
      return;
    const { error } = await supabase.from("testimonials").delete().eq("id", item.id);
    if (error) {
      alert(error.message);
      return;
    }
    const removals: Array<Promise<unknown>> = [];
    if (item.photo_path) {
      removals.push(supabase.storage.from("testimonials").remove([item.photo_path]));
    }
    if (item.video_poster_path) {
      removals.push(
        supabase.storage.from("testimonials").remove([item.video_poster_path]),
      );
    }
    if (item.video_path) {
      removals.push(
        supabase.storage.from("testimonial-videos").remove([item.video_path]),
      );
    }
    await Promise.all(removals);
    load();
  }


  return (
    <div>
      <header className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-extrabold md:text-4xl">Testimonials</h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            Stimmen von Kundinnen und Kunden für die Startseite. Veröffentlichte Einträge
            erscheinen sortiert im Slider.
          </p>
        </div>
        <button
          onClick={() => {
            setEditing(null);
            setCreatingKind(null);
            setPickingKind(true);
          }}
          className="inline-flex items-center gap-2 rounded-md bg-brand-button px-4 py-2 text-sm font-semibold text-brand-button-foreground"
        >
          <Plus className="h-4 w-4" /> Neu
        </button>
      </header>

      {error && (
        <div className="mt-6 rounded-md border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
          {error}
        </div>
      )}

      <div className="mt-8 flex flex-wrap gap-2">
        {(["all", ...ALL_CATEGORIES] as const).map((c) => {
          const active = categoryFilter === c;
          const label = c === "all" ? "Alle" : CATEGORY_LABELS[c];
          const count =
            c === "all"
              ? items.length
              : items.filter((t) => (t.categories ?? []).includes(c)).length;
          return (
            <button
              key={c}
              type="button"
              onClick={() => setCategoryFilter(c)}
              className={
                "rounded-full px-3 py-1 text-xs font-medium transition-colors " +
                (active
                  ? "bg-primary text-primary-foreground"
                  : "bg-secondary text-muted-foreground hover:bg-secondary/80")
              }
            >
              {label} <span className="opacity-70">({count})</span>
            </button>
          );
        })}
      </div>

      <AdminListToolbar
        search={query}
        onSearchChange={setQuery}
        searchPlaceholder="Nach Name, Rolle oder Zitat suchen …"
        searchLabel="Testimonials durchsuchen"
        count={visibleItems.length}
        total={items.length}
      />

      <div className="mt-4 overflow-hidden rounded-lg border border-border bg-background">
        {loading ? (
          <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Laden …
          </div>
        ) : visibleItems.length === 0 ? (
          <p className="p-6 text-sm text-muted-foreground">
            {items.length === 0
              ? "Noch keine Testimonials angelegt."
              : "Keine Testimonials passen zu Suche und Filter."}
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {visibleItems.map((t) => (
              <li key={t.id} className="flex items-start gap-4 p-4">
                {(() => {
                  const thumbPath = t.photo_path ?? t.video_poster_path ?? null;
                  return thumbPath ? (
                    <img
                      src={publicStorageUrl("testimonials", thumbPath) ?? ""}
                      alt=""
                      className="h-12 w-12 shrink-0 rounded-full object-cover"
                    />
                  ) : (
                    <div className="h-12 w-12 shrink-0 rounded-full bg-secondary" />
                  );
                })()}
                <div className="flex-1">
                  <p className="font-display text-sm font-extrabold">{t.author_name}</p>
                  <p className="text-xs text-muted-foreground">{t.author_role}</p>
                  {t.quote && (
                    <p className="mt-2 line-clamp-2 text-sm text-foreground/80">„{t.quote}"</p>
                  )}
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                    {t.media_kind === "video" ? (
                      <span className="inline-flex items-center gap-1 rounded bg-brand-button/20 px-2 py-0.5 text-foreground">
                        <Play className="h-3 w-3" fill="currentColor" /> Video
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded bg-secondary px-2 py-0.5 text-muted-foreground">
                        <Type className="h-3 w-3" /> Text
                      </span>
                    )}

                    {(t.categories ?? []).map((c) => (
                      <span
                        key={c}
                        className="rounded bg-secondary px-2 py-0.5 text-muted-foreground"
                      >
                        {CATEGORY_LABELS[c]}
                      </span>
                    ))}
                    <span className={"rounded px-2 py-0.5 font-medium " + PRIORITY_BADGE_CLASS[t.priority]}>
                      Priorität: {PRIORITY_LABELS[t.priority]}
                    </span>
                    <span
                      className={
                        "rounded px-2 py-0.5 " +
                        (t.published
                          ? "bg-brand-wahlen/20 text-foreground"
                          : "bg-muted text-muted-foreground")
                      }
                    >
                      {t.published ? "Veröffentlicht" : "Entwurf"}
                    </span>
                  </div>

                </div>
                <div className="flex shrink-0 gap-2">
                  <button
                    onClick={() => {
                      setCreatingKind(null);
                      setPickingKind(false);
                      setEditing(t);
                    }}
                    className="rounded border border-border px-3 py-1 text-xs font-semibold hover:bg-secondary"
                  >
                    Bearbeiten
                  </button>
                  <button
                    onClick={() => onDelete(t)}
                    aria-label="Löschen"
                    className="rounded border border-destructive/40 px-2 py-1 text-destructive hover:bg-destructive/10"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {pickingKind && (
        <KindPickerDialog
          onClose={() => setPickingKind(false)}
          onPick={(kind) => {
            setPickingKind(false);
            setCreatingKind(kind);
          }}
        />
      )}

      {(editing || creatingKind) && (
        <TestimonialForm
          initial={editing ?? emptyDraft(creatingKind ?? "photo")}
          isEdit={!!editing}
          onClose={() => {
            setEditing(null);
            setCreatingKind(null);
          }}
          onSaved={() => {
            setEditing(null);
            setCreatingKind(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function KindPickerDialog({
  onClose,
  onPick,
}: {
  onClose: () => void;
  onPick: (kind: MediaKind) => void;
}) {
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-foreground/40 p-4">
      <div className="w-full max-w-lg rounded-lg border border-border bg-background p-6 shadow-xl">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl font-extrabold">Testimonial-Typ wählen</h2>
          <button
            onClick={onClose}
            aria-label="Schließen"
            className="rounded p-1 text-muted-foreground hover:bg-secondary"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="mt-2 text-sm text-muted-foreground">
          Was möchten Sie anlegen?
        </p>
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <button
            onClick={() => onPick("photo")}
            className="group flex flex-col items-start gap-3 rounded-lg border border-border bg-background p-5 text-left transition-colors hover:border-primary hover:bg-secondary"
          >
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-secondary text-primary group-hover:bg-background">
              <Type className="h-5 w-5" />
            </span>
            <span className="font-display text-base font-extrabold">Text-Testimonial</span>
            <span className="text-xs text-muted-foreground">
              Klassisches Zitat mit Foto und Autor.
            </span>
          </button>
          <button
            onClick={() => onPick("video")}
            className="group flex flex-col items-start gap-3 rounded-lg border border-border bg-background p-5 text-left transition-colors hover:border-primary hover:bg-secondary"
          >
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-secondary text-primary group-hover:bg-background">
              <Video className="h-5 w-5" />
            </span>
            <span className="font-display text-base font-extrabold">Video-Testimonial</span>
            <span className="text-xs text-muted-foreground">
              Vimeo-Link statt lokalem Upload.
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}

function TestimonialForm({
  initial,
  isEdit,
  onClose,
  onSaved,
}: {
  initial: Testimonial | TestimonialInsert;
  isEdit: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [draft, setDraft] = useState<TestimonialInsert>({ ...initial });
  const [file, setFile] = useState<File | null>(null);
  const [posterFile, setPosterFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const mediaKind: MediaKind = draft.media_kind === "video" ? "video" : "photo";
  const vimeoRaw = (draft.vimeo_url ?? "").trim();
  const vimeoValidation = useMemo<VimeoValidation | null>(
    () => (vimeoRaw ? validateVimeoUrl(vimeoRaw) : null),
    [vimeoRaw],
  );
  const hasLegacyVideo = !!draft.video_path;
  const vimeoInvalid =
    mediaKind === "video" &&
    ((!vimeoRaw && !hasLegacyVideo) || (vimeoValidation !== null && !vimeoValidation.ok));

  function validateImage(f: File): string | null {
    if (!ALLOWED_IMAGE_MIME.includes(f.type)) {
      return "Bildformat nicht erlaubt (JPG, PNG oder WebP).";
    }
    if (f.size > MAX_IMAGE_BYTES) {
      return `Bild ist zu groß (max. ${Math.round(MAX_IMAGE_BYTES / 1024 / 1024)} MB).`;
    }
    return null;
  }

  async function uploadTo(
    bucket: string,
    file: File,
    previous: string | null,
  ): Promise<string> {
    // Vor dem Upload verkleinern und als WebP kodieren – hält die
    // Netzwerklast im Frontend klein.
    const optimized = await compressImageToWebp(file, { maxEdge: 1000, quality: 0.82 });
    const ext = optimized.name.split(".").pop() ?? "bin";
    const path = `${crypto.randomUUID()}.${ext}`;
    const { error: upErr } = await supabase.storage
      .from(bucket)
      .upload(path, optimized, { upsert: false, contentType: optimized.type });
    if (upErr) throw upErr;
    if (previous) {
      await supabase.storage.from(bucket).remove([previous]);
    }
    return path;
  }


  async function save() {
    setSaving(true);
    setErr(null);
    try {
      // Client-side validation
      if (file) {
        const v = validateImage(file);
        if (v) throw new Error(v);
      }
      if (posterFile) {
        const v = validateImage(posterFile);
        if (v) throw new Error(v);
      }
      if (mediaKind === "photo" && !draft.quote?.trim()) {
        throw new Error("Bitte ein Zitat eingeben.");
      }
      if (mediaKind === "video") {
        const trimmed = (draft.vimeo_url ?? "").trim();
        if (!trimmed && !draft.video_path) {
          throw new Error("Bitte einen Vimeo-Link hinterlegen.");
        }
        if (trimmed) {
          const v = validateVimeoUrl(trimmed);
          if (!v.ok) {
            throw new Error(v.message);
          }
        }
      }
      if (!draft.author_name?.trim()) {
        throw new Error("Bitte den Namen angeben.");
      }
      if ((draft.categories ?? []).length === 0) {
        throw new Error("Bitte mindestens eine Kategorie wählen.");
      }

      let photo_path = draft.photo_path ?? null;
      const video_path = draft.video_path ?? null;
      let video_poster_path = draft.video_poster_path ?? null;

      if (file) photo_path = await uploadTo("testimonials", file, photo_path);
      if (posterFile) {
        video_poster_path = await uploadTo("testimonials", posterFile, video_poster_path);
      }

      const payload: TestimonialInsert = {
        ...draft,
        media_kind: mediaKind,
        photo_path,
        video_path,
        video_poster_path,
        vimeo_url: mediaKind === "video" ? (draft.vimeo_url ?? "").trim() || null : null,
        ...(mediaKind === "video" ? { quote: null } : {}),
      };

      if (isEdit && "id" in initial) {
        const { error } = await supabase
          .from("testimonials")
          .update(payload)
          .eq("id", (initial as Testimonial).id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("testimonials").insert(payload);
        if (error) throw error;
      }
      const savedPriority = (payload.priority ?? "medium") as Priority;
      const authorLabel = (payload.author_name ?? "").trim() || "Testimonial";
      toast.success(isEdit ? "Testimonial aktualisiert" : "Testimonial angelegt", {
        description: `${authorLabel} · Priorität: ${PRIORITY_LABELS[savedPriority]}`,
        duration: 4000,
      });
      onSaved();
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      setErr(message);
      toast.error("Speichern fehlgeschlagen", { description: message });
    } finally {
      setSaving(false);
    }
  }


  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-foreground/40 p-4 md:p-10">
      <div className="w-full max-w-2xl rounded-lg border border-border bg-background p-6 shadow-xl">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl font-extrabold">
            {isEdit
              ? "Testimonial bearbeiten"
              : mediaKind === "video"
                ? "Neues Video-Testimonial"
                : "Neues Text-Testimonial"}
          </h2>
          <button
            onClick={onClose}
            aria-label="Schließen"
            className="rounded p-1 text-muted-foreground hover:bg-secondary"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {mediaKind === "video" && (
          <p className="mt-3 rounded-md border border-border bg-secondary/50 p-3 text-xs text-muted-foreground">
            Videos werden über den Vimeo-Player eingebettet. Bitte hinterlegen Sie den
            öffentlichen Vimeo-Link des Videos.
          </p>
        )}

        <div className="mt-6 space-y-4">
          {mediaKind !== "video" && (
            <Field label="Zitat">
              <textarea
                value={draft.quote ?? ""}
                onChange={(e) => setDraft({ ...draft, quote: e.target.value })}
                rows={4}
                className="w-full rounded-md border border-input bg-background p-3 text-sm"
              />
            </Field>
          )}
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Name">
              <input
                value={draft.author_name}
                onChange={(e) => setDraft({ ...draft, author_name: e.target.value })}
                className="w-full rounded-md border border-input bg-background p-2 text-sm"
              />
            </Field>
            <Field label="Funktion / Organisation">
              <input
                value={draft.author_role ?? ""}
                onChange={(e) => setDraft({ ...draft, author_role: e.target.value })}
                className="w-full rounded-md border border-input bg-background p-2 text-sm"
              />
            </Field>
          </div>
          <Field label="Kategorien (mehrere möglich)">
            <div className="flex flex-wrap gap-3">
              {ALL_CATEGORIES.map((c) => {
                const checked = (draft.categories ?? []).includes(c);
                return (
                  <label
                    key={c}
                    className="inline-flex items-center gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => {
                        const current = draft.categories ?? [];
                        const next = e.target.checked
                          ? Array.from(new Set([...current, c]))
                          : current.filter((x) => x !== c);
                        setDraft({ ...draft, categories: next });
                      }}
                    />
                    {CATEGORY_LABELS[c]}
                  </label>
                );
              })}
            </div>
            {(draft.categories ?? []).length === 0 && (
              <p className="mt-1 text-xs text-destructive">
                Mindestens eine Kategorie auswählen.
              </p>
            )}
          </Field>
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Priorität">
              <select
                value={draft.priority ?? "medium"}
                onChange={(e) =>
                  setDraft({ ...draft, priority: e.target.value as Priority })
                }
                className="w-full rounded-md border border-input bg-background p-2 text-sm"
              >
                <option value="high">Hoch</option>
                <option value="medium">Mittel</option>
                <option value="low">Tief</option>
              </select>
              <p className="mt-1 text-xs text-muted-foreground">
                „Hoch" erscheint vor „Mittel", „Mittel" vor „Tief" — unabhängig vom Filter.
              </p>
            </Field>
            <Field label="Veröffentlicht">
              <label className="flex h-9 items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={draft.published ?? false}
                  onChange={(e) => setDraft({ ...draft, published: e.target.checked })}
                />
                Sichtbar auf Startseite
              </label>
            </Field>
          </div>

          {isEdit && (
            <Field label="Medien-Typ (bei Bedarf umstellen)">
              <div className="flex flex-wrap gap-3">
                {(["photo", "video"] as const).map((k) => (
                  <label
                    key={k}
                    className="inline-flex items-center gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <input
                      type="radio"
                      name="media_kind"
                      checked={mediaKind === k}
                      onChange={() =>
                        setDraft({
                          ...draft,
                          media_kind: k,
                          ...(k === "video" ? { quote: "" } : {}),
                        })
                      }
                    />
                    {k === "photo" ? "Foto" : "Video"}
                  </label>
                ))}
              </div>
            </Field>
          )}

          <UploadField
            label={
              mediaKind === "video"
                ? "Foto der sprechenden Person (optional, für Karten-Kopf)"
                : "Foto (optional)"
            }
            hint="JPG, PNG oder WebP · max. 10 MB"
            icon={<FileImage className="h-5 w-5" />}
            accept="image/jpeg,image/png,image/webp"
            file={file}
            existingPath={draft.photo_path}
            onChange={(f) => setFile(f)}
          />

          {mediaKind === "video" && (
            <>
              <Field label="Vimeo-Link">
                <input
                  type="url"
                  inputMode="url"
                  placeholder="https://vimeo.com/123456789"
                  value={draft.vimeo_url ?? ""}
                  onChange={(e) => setDraft({ ...draft, vimeo_url: e.target.value })}
                  aria-invalid={vimeoValidation !== null && !vimeoValidation.ok}
                  className={`w-full rounded-md border bg-background p-2 text-sm ${
                    vimeoValidation !== null && !vimeoValidation.ok
                      ? "border-destructive"
                      : "border-input"
                  }`}
                />
                {vimeoValidation !== null && !vimeoValidation.ok && (
                  <>
                    <p className="mt-1 text-xs text-destructive">{vimeoValidation.message}</p>
                    {vimeoValidation.hint && (
                      <p className="mt-0.5 text-xs text-muted-foreground">{vimeoValidation.hint}</p>
                    )}
                  </>
                )}
                {vimeoValidation !== null && vimeoValidation.ok && (
                  <p className="mt-1 inline-flex items-center gap-1 text-xs text-brand-wahlen">
                    <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
                    Erkannt: Vimeo-ID {vimeoValidation.id}
                    {vimeoValidation.hash ? ` · Unlisted-Hash ${vimeoValidation.hash}` : ""}
                  </p>
                )}
                <p className="mt-1 text-xs text-muted-foreground">
                  Erlaubt: https://vimeo.com/123456789, .../video/123456789 sowie Unlisted-Links
                  im Format .../123456789/abcdef1234 bzw. mit ?h=abcdef1234.
                </p>
                {vimeoValidation !== null && vimeoValidation.ok && (
                  <div className="mt-3">
                    <div className="relative aspect-square w-full max-w-sm overflow-hidden rounded-md border border-border bg-black">
                      <iframe
                        key={`${vimeoValidation.id}-${vimeoValidation.hash ?? ""}`}
                        src={`https://player.vimeo.com/video/${vimeoValidation.id}?title=0&byline=0&portrait=0&dnt=1${vimeoValidation.hash ? `&h=${vimeoValidation.hash}` : ""}`}
                        title="Vimeo-Vorschau"
                        className="absolute left-1/2 top-1/2 h-[177.78%] w-[177.78%] -translate-x-1/2 -translate-y-1/2 border-0"
                        allow="fullscreen; picture-in-picture"
                        loading="lazy"
                        referrerPolicy="strict-origin-when-cross-origin"
                      />
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Vorschau im finalen Karten-Zuschnitt (1:1). Der Player greift auf vimeo.com zu.
                    </p>
                  </div>
                )}
              </Field>
              <UploadField
                label="Video-Poster (optional, sonst wird das Foto genutzt)"
                hint="JPG, PNG oder WebP · max. 10 MB"
                icon={<FileImage className="h-5 w-5" />}
                accept="image/jpeg,image/png,image/webp"
                file={posterFile}
                existingPath={draft.video_poster_path}
                onChange={(f) => setPosterFile(f)}
              />
            </>
          )}

        </div>

        {err && (
          <p className="mt-4 rounded border border-destructive/30 bg-destructive/10 p-3 text-xs text-destructive">
            {err}
          </p>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded-md border border-border px-4 py-2 text-sm font-semibold"
          >
            Abbrechen
          </button>
          <button
            onClick={save}
            disabled={saving || vimeoInvalid}
            title={vimeoInvalid ? "Bitte einen gültigen Vimeo-Link angeben." : undefined}
            className="inline-flex items-center gap-2 rounded-md bg-brand-button px-4 py-2 text-sm font-semibold text-brand-button-foreground disabled:opacity-60"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {saving ? "Speichern …" : "Speichern"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      {children}
    </label>
  );
}

function UploadField({
  label,
  hint,
  icon,
  accept,
  file,
  existingPath,
  onChange,
}: {
  label: string;
  hint: string;
  icon: React.ReactNode;
  accept: string;
  file: File | null;
  existingPath: string | null | undefined;
  onChange: (file: File | null) => void;
}) {
  const hasExisting = !!existingPath && !file;
  const fileName = existingPath ? existingPath.split("/").pop() : null;

  return (
    <div>
      <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <div className="rounded-md border border-border bg-secondary/40 p-3">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-background text-primary">
            {icon}
          </div>
          <div className="min-w-0 flex-1">
            {file ? (
              <>
                <p className="truncate text-sm font-semibold text-foreground">{file.name}</p>
                <p className="text-xs text-muted-foreground">
                  {(file.size / 1024 / 1024).toFixed(1)} MB · neu gewählt
                </p>
              </>
            ) : hasExisting ? (
              <>
                <p className="truncate text-sm font-semibold text-foreground">
                  Datei hinterlegt
                </p>
                <p className="truncate text-xs text-muted-foreground" title={fileName ?? ""}>
                  {fileName}
                </p>
              </>
            ) : (
              <>
                <p className="text-sm font-semibold text-foreground">
                  Noch keine Datei ausgewählt
                </p>
                <p className="text-xs text-muted-foreground">{hint}</p>
              </>
            )}
          </div>
          <label className="inline-flex shrink-0 cursor-pointer items-center gap-2 rounded-md bg-brand-button px-3 py-2 text-xs font-display font-extrabold uppercase tracking-wide text-brand-button-foreground transition-colors hover:bg-primary hover:text-primary-foreground">
            <Upload className="h-4 w-4" />
            {file || hasExisting ? "Ersetzen" : "Auswählen"}
            <input
              type="file"
              accept={accept}
              onChange={(e) => onChange(e.target.files?.[0] ?? null)}
              className="sr-only"
            />
          </label>
          {file && (
            <button
              type="button"
              onClick={() => onChange(null)}
              aria-label="Auswahl entfernen"
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-background hover:text-destructive"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        {(file || hasExisting) && (
          <p className="mt-2 text-xs text-muted-foreground">{hint}</p>
        )}
      </div>
    </div>
  );
}
